<script setup>
/* eslint-disable vue/no-bare-strings-in-template, vue/html-closing-bracket-newline, @intlify/vue-i18n/no-raw-text */
import { computed, onMounted, reactive, ref } from 'vue';
import { useAlert } from 'dashboard/composables';
import {
  currencyInputToDecimal,
  formatCurrencyInput,
} from 'shared/helpers/talkviaInputMasks';
import TalkviaCreditEnginesAPI from 'dashboard/api/talkviaCreditEngines';
import Button from 'dashboard/components-next/button/Button.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Select from 'dashboard/components-next/select/Select.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';

const operationOptions = [
  { value: 'new', label: 'Novo empréstimo' },
  { value: 'refinancing', label: 'Refinanciamento' },
  { value: 'portability', label: 'Portabilidade' },
];
const consignableSpecies = [
  1, 2, 3, 4, 5, 6, 7, 8, 11, 12, 18, 19, 20, 21, 22, 23, 24, 26, 27, 28, 29,
  30, 32, 33, 34, 37, 38, 40, 41, 42, 43, 44, 45, 46, 49, 51, 52, 54, 55, 56,
  57, 58, 59, 60, 72, 78, 81, 82, 83, 84, 89, 92, 93, 96,
];
const speciesPresets = [
  { id: 'custom', label: 'Personalizado', values: [] },
  { id: 'consignable', label: 'Consignáveis', values: consignableSpecies },
  { id: 'loas', label: 'LOAS', values: [87, 88] },
];
const allSpecies = Array.from({ length: 99 }, (_, index) => index + 1);

const engines = ref([]);
const loading = ref(false);
const saving = ref(false);
const activeSection = ref('banks');
const selectedId = ref(null);
const routeBankId = ref('');
const search = ref('');
const bankOptions = ref([]);
const selectedPreset = ref('custom');

const blankRoute = operation => ({
  id: `${operation}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
  operation,
  title: operationOptions.find(option => option.value === operation)?.label,
  description: '',
  minimum_paid_installments: 0,
  minimum_change: 0,
  minimum_balance: 0,
  own_bank_only: operation === 'refinancing',
  allow_without_change: false,
  best_condition: false,
  user_can_edit: true,
  active: true,
});

const blankForm = () => ({
  bank_name: '',
  code: '',
  priority: 100,
  active: true,
  eligibility_rules: {
    min_age: 18,
    max_age: 99,
    min_term: 12,
    max_term: 84,
    allowed_benefit_types: [],
  },
  metadata: {
    bank_code: '',
    min_new_amount: 0,
    coefficient_reference: '0.023500',
    highlight_new: false,
    highlight_refinancing: false,
    highlight_portability: false,
    user_can_edit: true,
    notes: '',
  },
  operational_steps: [blankRoute('new')],
});

const form = reactive(blankForm());
const filteredEngines = computed(() => {
  const term = search.value.trim().toLowerCase();
  if (!term) return engines.value;
  return engines.value.filter(engine =>
    `${engine.metadata?.bank_code || ''} ${engine.bank_name}`
      .toLowerCase()
      .includes(term)
  );
});
const acceptedSpecies = computed(() =>
  form.eligibility_rules.allowed_benefit_types.map(Number)
);
const bankSelectOptions = computed(() =>
  bankOptions.value.map(bank => ({
    value: bank.code,
    label: `${bank.code} - ${bank.name}`,
  }))
);
const selectedBankLabel = computed(() =>
  form.metadata.bank_code && form.bank_name
    ? `${form.metadata.bank_code} - ${form.bank_name}`
    : ''
);
const routeBankOptions = computed(() =>
  engines.value.map(engine => ({
    value: engine.id,
    label: `${engine.metadata?.bank_code || '--'} - ${engine.bank_name}`,
  }))
);
const minimumNewAmountInput = computed({
  get: () => `R$ ${formatCurrencyInput(form.metadata.min_new_amount || 0)}`,
  set: value => {
    form.metadata.min_new_amount = currencyInputToDecimal(value);
  },
});
const coefficientPercentageInput = computed({
  get: () => {
    const percentage = Number(form.metadata.coefficient_reference || 0) * 100;
    return `${new Intl.NumberFormat('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 6,
    }).format(percentage)}%`;
  },
  set: value => {
    const normalized = String(value || '')
      .replace(/[^\d,.-]/g, '')
      .replace(/\.(?=.*[,.])/g, '')
      .replace(',', '.');
    const percentage = Number(normalized);
    form.metadata.coefficient_reference = Number.isFinite(percentage)
      ? (percentage / 100).toFixed(6)
      : '';
  },
});
const routeCurrencyInput = (route, field) =>
  `R$ ${formatCurrencyInput(route[field] || 0)}`;
const updateRouteCurrency = (route, field, value) => {
  route[field] = currencyInputToDecimal(value);
};
const formValid = computed(() => {
  const minAge = Number(form.eligibility_rules.min_age);
  const maxAge = Number(form.eligibility_rules.max_age);
  const minTerm = Number(form.eligibility_rules.min_term);
  const maxTerm = Number(form.eligibility_rules.max_term);
  return (
    form.bank_name.trim() &&
    form.metadata.bank_code &&
    minAge <= maxAge &&
    minTerm <= maxTerm &&
    Number(form.metadata.coefficient_reference) > 0 &&
    (activeSection.value === 'banks' || form.operational_steps.length > 0)
  );
});

const load = async () => {
  loading.value = true;
  try {
    const { data } = await TalkviaCreditEnginesAPI.get();
    engines.value = data.engines || [];
  } catch {
    useAlert('Não foi possível carregar os bancos do motor de crédito.');
  } finally {
    loading.value = false;
  }
};

const resetForm = () => {
  Object.assign(form, blankForm());
  selectedId.value = null;
  routeBankId.value = '';
  selectedPreset.value = 'custom';
};

const editEngine = engine => {
  const fresh = blankForm();
  Object.assign(form, fresh, {
    bank_name: engine.bank_name,
    code: engine.code,
    priority: engine.priority,
    active: engine.active,
    eligibility_rules: {
      ...fresh.eligibility_rules,
      ...(engine.eligibility_rules || {}),
    },
    metadata: { ...fresh.metadata, ...(engine.metadata || {}) },
    operational_steps: (engine.operational_steps || []).map(route => ({
      ...blankRoute(route.operation || 'new'),
      ...route,
    })),
  });
  if (!form.operational_steps.length)
    form.operational_steps = [blankRoute('new')];
  selectedId.value = engine.id;
  routeBankId.value = engine.id;
  selectedPreset.value = 'custom';
};

const searchBanks = async (query = '') => {
  try {
    const { data } = await TalkviaCreditEnginesAPI.banks({ q: query.trim() });
    bankOptions.value = data.banks || [];
  } catch {
    bankOptions.value = [];
  }
};

const selectBankByCode = code => {
  const bank = bankOptions.value.find(option => option.code === code);
  if (!bank) return;
  form.bank_name = bank.name;
  form.metadata.bank_code = bank.code;
};

const selectRouteBank = id => {
  const engine = engines.value.find(item => item.id === Number(id));
  if (engine) editEngine(engine);
};

const startNewBank = () => {
  resetForm();
  activeSection.value = 'banks';
};

const applyPreset = preset => {
  selectedPreset.value = preset.id;
  form.eligibility_rules.allowed_benefit_types = preset.values.map(String);
};

const toggleSpecies = species => {
  selectedPreset.value = 'custom';
  const value = String(species);
  const current = form.eligibility_rules.allowed_benefit_types;
  form.eligibility_rules.allowed_benefit_types = current.includes(value)
    ? current.filter(item => item !== value)
    : [...current, value];
};

const addRoute = () => {
  form.operational_steps.push(blankRoute('refinancing'));
};

const removeRoute = index => {
  form.operational_steps.splice(index, 1);
};

const save = async () => {
  if (!formValid.value) {
    useAlert('Revise banco, faixas, coeficiente e ao menos um roteiro.');
    return;
  }
  saving.value = true;
  const payload = {
    bank_name: form.bank_name,
    name: `${form.metadata.bank_code} - ${form.bank_name}`,
    code: form.code,
    product_type: 'Consignado INSS',
    agreement_type: 'INSS',
    active: form.active,
    priority: Number(form.priority),
    eligibility_rules: {
      ...form.eligibility_rules,
      min_age: Number(form.eligibility_rules.min_age),
      max_age: Number(form.eligibility_rules.max_age),
      min_term: Number(form.eligibility_rules.min_term),
      max_term: Number(form.eligibility_rules.max_term),
    },
    metadata: { ...form.metadata },
    operational_steps: form.operational_steps.map(route => ({
      ...route,
      minimum_paid_installments: Number(route.minimum_paid_installments || 0),
      minimum_change: Number(route.minimum_change || 0),
      minimum_balance: Number(route.minimum_balance || 0),
    })),
    funnel_stages: [],
  };
  try {
    const response = selectedId.value
      ? await TalkviaCreditEnginesAPI.update(selectedId.value, payload)
      : await TalkviaCreditEnginesAPI.create(payload);
    const index = engines.value.findIndex(item => item.id === response.data.id);
    if (index >= 0) engines.value.splice(index, 1, response.data);
    else engines.value.unshift(response.data);
    editEngine(response.data);
    useAlert('Banco e roteiros salvos no motor de crédito.', {
      variant: 'positive',
    });
  } catch (error) {
    useAlert(
      error.response?.data?.error ||
        'Não foi possível salvar esta configuração.'
    );
  } finally {
    saving.value = false;
  }
};

const operationLabel = operation =>
  operationOptions.find(option => option.value === operation)?.label ||
  operation;

onMounted(() => {
  load();
  searchBanks();
});
</script>

<template>
  <main class="h-full min-h-0 overflow-y-auto bg-n-background p-4 sm:p-6">
    <div class="mx-auto grid max-w-7xl gap-5 pb-24">
      <header class="flex flex-wrap items-end justify-between gap-4">
        <div class="max-w-3xl">
          <span
            class="text-xs font-semibold uppercase tracking-wide text-n-brand"
          >
            Benefício 360
          </span>
          <h1 class="mt-1 text-2xl font-semibold text-n-slate-12">
            Motor de crédito
          </h1>
          <p class="mt-1 text-sm text-n-slate-10">
            Cadastre as condições dos bancos e organize os roteiros operacionais
            em uma área independente.
          </p>
        </div>
        <Button
          :label="
            activeSection === 'banks' ? 'Cadastrar banco' : 'Adicionar roteiro'
          "
          icon="i-lucide-plus"
          :disabled="activeSection === 'routes' && !selectedId"
          @click="activeSection === 'banks' ? startNewBank() : addRoute()"
        />
      </header>

      <nav
        class="grid gap-3 md:grid-cols-2"
        aria-label="Áreas do motor de crédito"
      >
        <button
          type="button"
          class="flex items-center gap-4 rounded-xl border p-4 text-left transition-colors"
          :class="
            activeSection === 'banks'
              ? 'border-n-brand bg-n-brand/10'
              : 'border-n-weak bg-n-solid-1 hover:border-n-strong'
          "
          @click="activeSection = 'banks'"
        >
          <span
            class="grid size-10 place-items-center rounded-lg bg-n-brand/15 text-n-brand"
          >
            <i class="i-lucide-landmark size-5" />
          </span>
          <span>
            <strong class="block text-sm text-n-slate-12"
              >Cadastro de bancos</strong
            >
            <small class="text-xs text-n-slate-10"
              >Faixas, espécies, coeficiente e exibição</small
            >
          </span>
        </button>
        <button
          type="button"
          class="flex items-center gap-4 rounded-xl border p-4 text-left transition-colors"
          :class="
            activeSection === 'routes'
              ? 'border-n-brand bg-n-brand/10'
              : 'border-n-weak bg-n-solid-1 hover:border-n-strong'
          "
          @click="activeSection = 'routes'"
        >
          <span
            class="grid size-10 place-items-center rounded-lg bg-n-brand/15 text-n-brand"
          >
            <i class="i-lucide-route size-5" />
          </span>
          <span>
            <strong class="block text-sm text-n-slate-12"
              >Roteiros operacionais</strong
            >
            <small class="text-xs text-n-slate-10"
              >Novo, refinanciamento e portabilidade</small
            >
          </span>
        </button>
      </nav>

      <section
        class="grid gap-5"
        :class="
          activeSection === 'banks' ? 'xl:grid-cols-[320px_minmax(0,1fr)]' : ''
        "
      >
        <aside
          v-if="activeSection === 'banks'"
          class="overflow-hidden rounded-xl border border-n-weak bg-n-solid-1"
        >
          <div class="border-b border-n-weak p-4">
            <h2 class="font-semibold text-n-slate-12">Bancos cadastrados</h2>
            <p class="mb-3 text-xs text-n-slate-10">
              {{ engines.length }} configuração(ões)
            </p>
            <Input v-model="search" type="search" placeholder="Buscar banco" />
          </div>
          <div class="max-h-[68vh] space-y-2 overflow-y-auto p-3">
            <p v-if="loading" class="p-3 text-sm text-n-slate-10">
              Carregando...
            </p>
            <p
              v-else-if="!filteredEngines.length"
              class="p-3 text-sm text-n-slate-10"
            >
              Nenhum banco cadastrado.
            </p>
            <button
              v-for="engine in filteredEngines"
              :key="engine.id"
              type="button"
              class="w-full rounded-lg border p-3 text-left"
              :class="
                selectedId === engine.id
                  ? 'border-n-brand bg-n-brand/10'
                  : 'border-n-weak hover:border-n-strong'
              "
              @click="editEngine(engine)"
            >
              <div class="flex items-start justify-between gap-2">
                <div>
                  <strong class="text-sm text-n-slate-12">
                    {{ engine.metadata?.bank_code }} - {{ engine.bank_name }}
                  </strong>
                  <p class="mt-1 text-xs text-n-slate-10">
                    Prioridade {{ engine.priority }} ·
                    {{ engine.operational_steps?.length || 0 }} roteiro(s)
                  </p>
                </div>
                <span
                  class="rounded-full px-2 py-0.5 text-[11px] font-semibold"
                  :class="
                    engine.active
                      ? 'bg-n-teal-3 text-n-teal-11'
                      : 'bg-n-slate-3 text-n-slate-10'
                  "
                  >{{ engine.active ? 'Ativo' : 'Inativo' }}</span
                >
              </div>
            </button>
          </div>
        </aside>

        <form class="grid gap-5" @submit.prevent="save">
          <section
            v-if="activeSection === 'banks'"
            class="rounded-xl border border-n-weak bg-n-solid-1 p-5"
          >
            <div class="mb-5 flex flex-wrap items-start justify-between gap-3">
              <div>
                <h2 class="text-lg font-semibold text-n-slate-12">
                  {{ selectedId ? 'Editar banco' : 'Cadastrar banco' }}
                </h2>
                <p class="text-xs text-n-slate-10">
                  Condições básicas usadas na leitura do Benefício 360.
                </p>
              </div>
              <label
                class="flex items-center gap-3 rounded-lg border border-n-weak px-3 py-2 text-sm font-medium"
              >
                <Switch v-model="form.active" />
                Banco ativo
              </label>
            </div>

            <div class="mb-5">
              <label class="mb-1.5 block text-sm font-medium text-n-slate-12"
                >Banco</label
              >
              <ComboBox
                v-model="form.metadata.bank_code"
                :options="bankSelectOptions"
                :display-label="selectedBankLabel"
                placeholder="Selecione um banco"
                search-placeholder="Buscar por código ou nome"
                empty-state="Nenhum banco encontrado"
                use-api-results
                teleport-to-body
                @open="searchBanks('')"
                @search="searchBanks"
                @update:model-value="selectBankByCode"
              />
            </div>

            <div class="mb-3">
              <h3 class="text-sm font-semibold text-n-slate-12">
                Público e prazo
              </h3>
              <p class="text-xs text-n-slate-10">
                Condições que definem quando o banco participa do cálculo.
              </p>
            </div>
            <div class="grid gap-4 lg:grid-cols-2">
              <article class="rounded-xl border border-n-weak bg-n-alpha-1 p-4">
                <div class="mb-4 flex items-center gap-3">
                  <span
                    class="grid size-9 place-items-center rounded-lg bg-n-teal-3 text-n-teal-11"
                  >
                    <i class="i-lucide-users-round size-4" />
                  </span>
                  <div>
                    <h4 class="text-sm font-semibold text-n-slate-12">
                      Faixa de idade
                    </h4>
                    <p class="text-xs text-n-slate-10">
                      Idade permitida para contratação.
                    </p>
                  </div>
                </div>
                <div class="grid gap-4 sm:grid-cols-2">
                  <Input
                    v-model="form.eligibility_rules.min_age"
                    label="Idade mínima"
                    type="number"
                    min="0"
                    max="120"
                  />
                  <Input
                    v-model="form.eligibility_rules.max_age"
                    label="Idade máxima"
                    type="number"
                    min="0"
                    max="120"
                  />
                </div>
              </article>
              <article class="rounded-xl border border-n-weak bg-n-alpha-1 p-4">
                <div class="mb-4 flex items-center gap-3">
                  <span
                    class="grid size-9 place-items-center rounded-lg bg-n-blue-3 text-n-blue-11"
                  >
                    <i class="i-lucide-calendar-range size-4" />
                  </span>
                  <div>
                    <h4 class="text-sm font-semibold text-n-slate-12">
                      Faixa de prazo
                    </h4>
                    <p class="text-xs text-n-slate-10">
                      Quantidade de parcelas aceita pelo banco.
                    </p>
                  </div>
                </div>
                <div class="grid gap-4 sm:grid-cols-2">
                  <Input
                    v-model="form.eligibility_rules.min_term"
                    label="Prazo mínimo"
                    type="number"
                    min="0"
                    message="Quantidade mínima de parcelas."
                  />
                  <Input
                    v-model="form.eligibility_rules.max_term"
                    label="Prazo máximo"
                    type="number"
                    min="0"
                    message="Quantidade máxima de parcelas."
                  />
                </div>
              </article>
            </div>

            <div class="mb-3 mt-6 border-t border-n-weak pt-5">
              <h3 class="text-sm font-semibold text-n-slate-12">
                Condições comerciais
              </h3>
              <p class="text-xs text-n-slate-10">
                Valores de referência e ordem de apresentação ao operador.
              </p>
            </div>
            <div class="grid gap-4 md:grid-cols-3">
              <div class="flex min-w-0 flex-col gap-1">
                <div class="mb-0.5 flex items-center gap-1.5">
                  <label
                    for="credit-engine-minimum-new"
                    class="text-heading-3 text-n-slate-12"
                  >
                    Valor mínimo para novo empréstimo
                  </label>
                  <button
                    v-tooltip.top="
                      'Menor valor que o banco aceita liberar em uma operação nova.'
                    "
                    type="button"
                    class="shrink-0 text-n-slate-10 hover:text-n-slate-12"
                    aria-label="Explicação do valor mínimo para novo empréstimo"
                  >
                    <i class="i-lucide-info size-4" />
                  </button>
                </div>
                <Input
                  id="credit-engine-minimum-new"
                  v-model="minimumNewAmountInput"
                  inputmode="decimal"
                />
              </div>
              <div class="flex min-w-0 flex-col gap-1">
                <div class="mb-0.5 flex items-center gap-1.5">
                  <label
                    for="credit-engine-coefficient"
                    class="text-heading-3 text-n-slate-12"
                  >
                    Coeficiente de referência
                  </label>
                  <button
                    v-tooltip.top="
                      'Informe o coeficiente em porcentagem. Exemplo: 2,35%.'
                    "
                    type="button"
                    class="shrink-0 text-n-slate-10 hover:text-n-slate-12"
                    aria-label="Explicação do coeficiente de referência"
                  >
                    <i class="i-lucide-info size-4" />
                  </button>
                </div>
                <Input
                  id="credit-engine-coefficient"
                  v-model="coefficientPercentageInput"
                  inputmode="decimal"
                />
              </div>
              <div class="flex min-w-0 flex-col gap-1">
                <div class="mb-0.5 flex items-center gap-1.5">
                  <label
                    for="credit-engine-priority"
                    class="text-heading-3 text-n-slate-12"
                  >
                    Prioridade de exibição
                  </label>
                  <button
                    v-tooltip.top="
                      'Define a ordem dos bancos no resultado: quanto menor o número, mais cedo o banco aparece. O padrão é 100.'
                    "
                    type="button"
                    class="shrink-0 text-n-slate-10 hover:text-n-slate-12"
                    aria-label="Explicação da prioridade de exibição"
                  >
                    <i class="i-lucide-info size-4" />
                  </button>
                </div>
                <Input
                  id="credit-engine-priority"
                  v-model="form.priority"
                  type="number"
                  min="0"
                />
              </div>
            </div>

            <div class="mt-6 border-t border-n-weak pt-5">
              <div class="flex items-end justify-between gap-3">
                <div>
                  <h3 class="text-sm font-semibold text-n-slate-12">
                    Espécies aceitas
                  </h3>
                  <p class="text-xs text-n-slate-10">
                    Use um grupo pronto ou marque individualmente.
                  </p>
                </div>
                <span class="text-xs font-medium text-n-brand"
                  >{{ acceptedSpecies.length }} selecionada(s)</span
                >
              </div>
              <div class="mt-2 grid grid-cols-3 gap-2">
                <button
                  v-for="preset in speciesPresets"
                  :key="preset.id"
                  type="button"
                  class="rounded-lg border px-3 py-2 text-sm font-medium"
                  :class="
                    selectedPreset === preset.id
                      ? 'border-n-brand bg-n-brand text-white'
                      : 'border-n-weak text-n-slate-11'
                  "
                  @click="applyPreset(preset)"
                >
                  {{ preset.label }}
                </button>
              </div>
              <div
                class="mt-3 grid max-h-64 grid-cols-[repeat(auto-fill,minmax(62px,1fr))] gap-2 overflow-y-auto rounded-lg border border-n-weak bg-n-alpha-1 p-3"
              >
                <label
                  v-for="species in allSpecies"
                  :key="species"
                  class="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-xs hover:bg-n-alpha-2"
                >
                  <Checkbox
                    :model-value="acceptedSpecies.includes(species)"
                    @change="toggleSpecies(species)"
                  />
                  {{ String(species).padStart(2, '0') }}
                </label>
              </div>
            </div>

            <div class="mb-3 mt-6 border-t border-n-weak pt-5">
              <h3 class="text-sm font-semibold text-n-slate-12">
                Exibição para o operador
              </h3>
              <p class="text-xs text-n-slate-10">
                Escolha quais oportunidades merecem destaque no Benefício 360.
              </p>
            </div>
            <div class="grid gap-3 md:grid-cols-2">
              <label
                class="flex cursor-pointer items-center gap-3 rounded-lg border border-n-weak p-3 text-sm"
                ><Checkbox v-model="form.metadata.highlight_new" />Destacar novo
                empréstimo</label
              >
              <label
                class="flex cursor-pointer items-center gap-3 rounded-lg border border-n-weak p-3 text-sm"
                ><Checkbox
                  v-model="form.metadata.highlight_refinancing"
                />Destacar refinanciamento</label
              >
              <label
                class="flex cursor-pointer items-center gap-3 rounded-lg border border-n-weak p-3 text-sm"
                ><Checkbox
                  v-model="form.metadata.highlight_portability"
                />Destacar portabilidade</label
              >
              <label
                class="flex cursor-pointer items-center gap-3 rounded-lg border border-n-weak p-3 text-sm"
                ><Checkbox v-model="form.metadata.user_can_edit" />Operador pode
                ajustar o coeficiente</label
              >
            </div>
            <TextArea
              v-model="form.metadata.notes"
              class="mt-4"
              label="Observações"
              placeholder="Restrições ou orientações gerais do banco"
            />
          </section>

          <section
            v-if="activeSection === 'routes'"
            class="rounded-xl border border-n-weak bg-n-solid-1 p-5"
          >
            <div
              class="grid gap-4 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-end"
            >
              <div>
                <label class="mb-1.5 block text-sm font-medium text-n-slate-12"
                  >Banco dos roteiros</label
                >
                <ComboBox
                  v-model="routeBankId"
                  :options="routeBankOptions"
                  placeholder="Selecione um banco cadastrado"
                  search-placeholder="Buscar banco cadastrado"
                  empty-state="Nenhum banco cadastrado"
                  teleport-to-body
                  @update:model-value="selectRouteBank"
                />
              </div>
              <Button
                type="button"
                label="Cadastrar novo banco"
                icon="i-lucide-plus"
                color="slate"
                variant="outline"
                @click="startNewBank"
              />
            </div>
          </section>

          <section
            v-if="activeSection === 'routes' && !selectedId"
            class="rounded-xl border border-dashed border-n-weak bg-n-solid-1 p-10 text-center"
          >
            <span
              class="mx-auto grid size-12 place-items-center rounded-full bg-n-alpha-2 text-n-slate-10"
            >
              <i class="i-lucide-route size-6" />
            </span>
            <h2 class="mt-3 font-semibold text-n-slate-12">
              Selecione um banco
            </h2>
            <p class="mt-1 text-sm text-n-slate-10">
              Cada banco mantém seus próprios roteiros de novo, refinanciamento
              e portabilidade.
            </p>
          </section>

          <section
            v-if="activeSection === 'routes' && selectedId"
            class="rounded-xl border border-n-weak bg-n-solid-1 p-5"
          >
            <div class="mb-4 flex flex-wrap items-center justify-between gap-3">
              <div>
                <h2 class="text-lg font-semibold text-n-slate-12">
                  Roteiros de {{ form.metadata.bank_code }} -
                  {{ form.bank_name }}
                </h2>
                <p class="text-xs text-n-slate-10">
                  Regras exclusivas deste banco para orientar o operador.
                </p>
              </div>
              <Button
                type="button"
                label="Adicionar roteiro"
                icon="i-lucide-plus"
                color="slate"
                variant="outline"
                @click="addRoute"
              />
            </div>
            <div class="grid gap-3">
              <article
                v-for="(route, index) in form.operational_steps"
                :key="route.id"
                class="rounded-xl border border-n-weak bg-n-alpha-1 p-4 sm:p-5"
              >
                <div class="mb-3 flex items-center justify-between gap-3">
                  <strong class="text-sm text-n-slate-12"
                    >Roteiro {{ index + 1 }} ·
                    {{ operationLabel(route.operation) }}</strong
                  >
                  <button
                    v-if="form.operational_steps.length > 1"
                    type="button"
                    class="text-xs font-medium text-n-ruby-11"
                    @click="removeRoute(index)"
                  >
                    Remover
                  </button>
                </div>
                <Input
                  v-model="route.title"
                  class="mb-4"
                  label="Título do roteiro"
                  placeholder="Ex.: Refinanciamento com troco"
                />
                <div class="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                  <div class="flex flex-col gap-1">
                    <label class="text-sm font-medium">Operação</label
                    ><Select
                      v-model="route.operation"
                      :options="operationOptions"
                    />
                  </div>
                  <Input
                    v-model="route.minimum_paid_installments"
                    label="Parcelas pagas mín."
                    type="number"
                    min="0"
                  />
                  <Input
                    :model-value="routeCurrencyInput(route, 'minimum_change')"
                    label="Troco mínimo"
                    inputmode="decimal"
                    @update:model-value="
                      value =>
                        updateRouteCurrency(route, 'minimum_change', value)
                    "
                  />
                  <Input
                    :model-value="routeCurrencyInput(route, 'minimum_balance')"
                    label="Saldo mínimo"
                    inputmode="decimal"
                    @update:model-value="
                      value =>
                        updateRouteCurrency(route, 'minimum_balance', value)
                    "
                  />
                </div>
                <TextArea
                  v-model="route.description"
                  class="mt-3"
                  label="Orientação para o operador"
                  placeholder="Documentos, tabela, canal e cuidados da formalização"
                />
                <div class="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                  <label
                    class="flex cursor-pointer items-center gap-3 rounded-lg border border-n-weak bg-n-solid-1 p-3 text-sm"
                    ><Checkbox v-model="route.own_bank_only" />Contrato do
                    próprio banco</label
                  >
                  <label
                    class="flex cursor-pointer items-center gap-3 rounded-lg border border-n-weak bg-n-solid-1 p-3 text-sm"
                    ><Checkbox v-model="route.allow_without_change" />Permitir
                    sem troco</label
                  >
                  <label
                    class="flex cursor-pointer items-center gap-3 rounded-lg border border-n-weak bg-n-solid-1 p-3 text-sm"
                    ><Checkbox v-model="route.best_condition" />Melhor
                    condição</label
                  >
                  <label
                    class="flex cursor-pointer items-center gap-3 rounded-lg border border-n-weak bg-n-solid-1 p-3 text-sm"
                    ><Checkbox v-model="route.user_can_edit" />Operador pode
                    alterar</label
                  >
                  <label
                    class="flex items-center justify-between gap-3 rounded-lg border border-n-weak bg-n-solid-1 p-3 text-sm font-medium md:col-span-2"
                    >Roteiro disponível<Switch v-model="route.active"
                  /></label>
                </div>
              </article>
            </div>
          </section>

          <footer
            v-if="activeSection === 'banks' || selectedId"
            class="flex justify-end gap-3 rounded-xl border border-n-weak bg-n-solid-1 p-4"
          >
            <Button
              type="button"
              :label="
                activeSection === 'banks' ? 'Limpar' : 'Voltar aos bancos'
              "
              color="slate"
              variant="outline"
              @click="
                activeSection === 'banks'
                  ? resetForm()
                  : (activeSection = 'banks')
              "
            />
            <Button
              type="submit"
              :label="
                activeSection === 'banks'
                  ? selectedId
                    ? 'Atualizar banco'
                    : 'Salvar banco'
                  : 'Salvar roteiros'
              "
              icon="i-lucide-save"
              :disabled="!formValid"
              :is-loading="saving"
            />
          </footer>
        </form>
      </section>
    </div>
  </main>
</template>
